/**
 * Account projects and machine checkouts (docs/ux/workflow-simplification.md §E).
 *
 * A project is an account-level encrypted entity (name, goal, remote, shared settings). Machines
 * hold checkouts: the local path and the machine-local execution policy live ONLY on the machine
 * (`<viko root>/checkouts.json`) and reach devices only inside the encrypted `machine.projects`
 * report. Cloning or creating a repo is a passkey-approved `CheckoutStatement` (`@viko/crypto`);
 * binding a checkout the user already registered locally is a device-signed `project.adopt`.
 * Additive only: every schema here is new or an optional extension.
 */
import { z } from 'zod';
import { PrivateEntityId } from './encrypted-entities.js';
import { WebAuthnAssertion } from './signing.js';
const Text = (max) => z.string().min(1).max(max);
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
/** Directory / repo name: lowercase letters, digits, dashes, dots and underscores. */
export const CheckoutDirName = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9][a-z0-9._-]*$/, 'lowercase letters, digits, dots, dashes and underscores')
    .refine((s) => s !== '.' && s !== '..' && !s.includes('..'), 'not a relative path');
/** GitHub owner / repo name (GitHub's own character set). */
const GhName = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, 'letters, digits, dots, dashes and underscores');
/** Mirrors `@viko/crypto` CheckoutStatement (the crypto package validates it again). */
export const CheckoutStatementSchema = z.strictObject({
    version: z.literal(1),
    type: z.literal('viko.project-checkout'),
    accountId: Text(256),
    statementId: Text(256),
    projectId: PrivateEntityId,
    machineIds: z.array(Text(128)).min(1).max(8),
    remoteDigest: Text(64),
    configAuthor: Text(128).optional(),
    issuedAt: Ts,
    expiresAt: Ts,
    deviceKeyId: Text(256),
});
/** Capability tags lanes and commands `require` (v2 config), e.g. `platform:mac`, `xcode`. */
export const CapabilityTag = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9:._-]*$/);
/** Machine-local execution policy for one checkout. Never committed, never plaintext. */
export const LocalPolicy = z.strictObject({
    worktreeRoot: Text(4096).optional(),
    poolSize: z.number().int().min(1).max(32).optional(),
    /** This machine may run landings (default true for the first checkout). */
    landing: z.boolean(),
    /** Lane ids pinned here; empty = every lane whose `requires` this machine satisfies. */
    lanes: z.array(Text(128)).max(64),
    /** Secret-command names this machine may run. */
    secretCommands: z.array(Text(128)).max(64),
});
export const ProjectCreateTarget = z.strictObject({
    provider: z.literal('github'),
    owner: GhName,
    name: GhName,
    visibility: z.literal('private'),
});
export const ACCOUNT_PROJECT_COMMAND_PAYLOADS = {
    /**
     * Check the project out on this machine (adopt a matching clone, clone, or create the repo).
     * Risky: the step-up is the passkey-approved `statement` shared by every listed machine.
     */
    'project.checkout': z
        .strictObject({
        projectId: PrivateEntityId,
        statement: CheckoutStatementSchema,
        assertion: WebAuthnAssertion.strict(),
        remote: Text(2048).optional(),
        create: ProjectCreateTarget.optional(),
        dirName: CheckoutDirName,
        /** Adopt this locally registered checkout (must still match the remote). */
        adoptPath: Text(4096).optional(),
        /** For the config author's shared-config draft; encrypted like everything else here. */
        name: Text(256).optional(),
        goal: z.string().max(4000).optional(),
        // ---- g5-setup: the shared config the user reviewed while scoping (config author only) ----
        config: z.string().min(1).max(64_000).optional(),
        // ---- end g5-setup ----
    })
        .refine((p) => !!p.remote !== !!p.create, 'exactly one of remote or create')
        .refine((p) => p.statement.projectId === p.projectId, 'statement project mismatch'),
    /** Bind a checkout ALREADY registered on this machine (a local user act). Clones nothing. */
    'project.adopt': z.strictObject({
        projectId: PrivateEntityId,
        localPath: Text(4096),
    }),
    /** Remove the mapping. Never deletes files. */
    'project.forget': z.strictObject({ projectId: PrivateEntityId }),
    /**
     * The "Migrate viko.project.yaml" card: propose the v1 → v2 conversion of this checkout's file
     * on a Viko branch through landing (auto on `fast`, a tap otherwise). Never forced.
     */
    'project.migrate_config': z.strictObject({ projectId: PrivateEntityId }),
    /** Machine-local policy edits from the app (risky). */
    'project.policy': z.strictObject({
        projectId: PrivateEntityId,
        local: LocalPolicy.partial(),
    }),
};
export const ProjectCheckoutResult = z.strictObject({
    status: z.enum(['adopted', 'cloned', 'created', 'needs-gh-auth', 'conflict', 'failed']),
    path: Text(4096).optional(),
    remote: Text(2048).optional(),
    head: Text(64).optional(),
    configCommit: Text(64).optional(),
    landingId: Text(256).optional(),
    reason: z.string().max(1000).optional(),
});
/** One checkout in the encrypted `machine.projects` report (frame v2). */
export const MachineCheckout = z.strictObject({
    projectId: PrivateEntityId,
    path: Text(4096),
    remote: z.string().max(2048),
    head: z.string().max(64),
    dirty: z.boolean(),
    configVersion: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    lanes: z.array(Text(128)).max(64),
    landing: z.boolean(),
    via: z.enum(['checkout', 'adopt-local']),
});
/** A repository on the machine that is not bound to an account project yet. */
export const MachineCandidate = z.strictObject({
    path: Text(4096),
    remote: z.string().max(2048).optional(),
    name: Text(256),
    /** Offered by `viko setup` inside this repo ("Add this repo as a project?"). */
    offered: z.boolean().optional(),
});
/** Facts the PWA uses for defaults (dev root, `gh` user). Learned locally, reported encrypted. */
export const MachineProjectDefaults = z.strictObject({
    devRoot: Text(4096),
    ghUser: Text(100).optional(),
});
//# sourceMappingURL=account-projects.js.map